import type { TournamentCompetition, TournamentMatch } from "@mixitone/models";
import { List } from "@mixitone/oom";
import { isNil, isPresent, pick } from "@mixitone/util";
import MixitoneController from "../../../../controllers/MixitoneController";
import { TournamentDataAdapter } from "tournament-formats/TournamentDataAdapter";
import TournamentController from "../TournamentController";
import { roundMatchesResolved } from "../tournamentHelpers";

interface State {
  loading: boolean;
  generating: boolean;
  competitions: List<TournamentCompetition>;
  selectedCompetitionId?: string;
  matches: TournamentMatch[];
  matchGroups: Record<string, MatchGroup>;
  matchesByRound: RoundMatchesWithMetadata[][];
  highlightEntryId?: string;
  completedRoundsExpanded: boolean;
}

interface MatchGroup {
  matches: TournamentMatch[];
  matchCountByRound: number[];
  maxByRounds: number;
  // The row in the grid where this group starts. This is needed because the
  // group can span multiple rounds and we want to align them horizontally
  row: number;
  rounds: RoundMatchesWithMetadata[];
  metadata: Record<string, any>;
  key: string;
}

interface RoundMatchesWithMetadata {
  round: number;
  matches: TournamentMatch[];
  groupKey: string;
}

interface Props {
  params: {
    id: string;
  };
}

export class TournamentMatchesController extends MixitoneController<State, Props> {
  get initialState(): State {
    return {
      loading: true,
      generating: false,
      competitions: new List(),
      matches: [],
      matchGroups: {},
      matchesByRound: [],
      completedRoundsExpanded: false,
    };
  }

  get tournamentController() {
    return this.get(TournamentController);
  }

  override async initialize(props: Props) {
    await this.changeProps(props);

    // Observe tournament session and its competitions
    this.observe(() => {
      if (this.state.generating) return;
      if (this.tournamentController.state.loading) return;

      this.state.loading = false;
      this.state.competitions = this.tournamentController.state.competitions;
    });

    this.observe(() => {
      const nextMatches = this.tournamentController.state.matches
        .filter((m) => m.tournament_competition_id === this.state.selectedCompetitionId)
        .toSorted((a, b) => {
          const aRound = a.round || 0;
          const bRound = b.round || 0;

          if (aRound === bRound) {
            const aMatchIndex = a.match_index || 0;
            const bMatchIndex = b.match_index || 0;
            return aMatchIndex - bMatchIndex;
          }

          return aRound - bRound;
        });

      const matchesByRound: RoundMatchesWithMetadata[][] = [];
      const matchGroups: Record<string, MatchGroup> = {};

      let lastRoundWithMetadata: RoundMatchesWithMetadata | undefined;
      const sharedMetadataKeys = ["division"];
      const getSharedMetadata = (match: TournamentMatch) => {
        return pick(match.format_data || {}, sharedMetadataKeys);
      };
      const generateGroupKey = (metadata: Record<string, any>) => {
        return JSON.stringify(metadata || {});
      };

      let row = -1;
      for (const match of nextMatches) {
        if (isNil(match.round)) continue;
        if (lastRoundWithMetadata && lastRoundWithMetadata.round === match.round) {
          row += 1;
        } else {
          row = 0;
        }

        const sharedMetadata = getSharedMetadata(match);
        const groupKey = generateGroupKey(sharedMetadata);
        const matchGroup = matchGroups[groupKey];

        if (matchGroup) {
          matchGroup.matches.push(match);
          matchGroup.matchCountByRound[match.round] = (matchGroup.matchCountByRound[match.round] || 0) + 1;

          // Update the row if this is the first match in the group for this round
          if (matchGroup.matchCountByRound[match.round] === 1) {
            matchGroup.row = Math.max(matchGroup.row, row);
          }
        } else {
          matchGroups[groupKey] = {
            rounds: [],
            matchCountByRound: [],
            maxByRounds: 0,
            row,
            matches: [match],
            metadata: sharedMetadata,
            key: groupKey,
          };
          matchGroups[groupKey].matchCountByRound[match.round] = 1;
        }

        if (
          lastRoundWithMetadata &&
          lastRoundWithMetadata.groupKey === groupKey &&
          lastRoundWithMetadata.round === match.round
        ) {
          lastRoundWithMetadata.matches.push(match);
        } else {
          lastRoundWithMetadata = {
            round: match.round,
            matches: [match],
            groupKey,
          };

          const matchGroup = matchGroups[groupKey];
          matchGroup.rounds.push(lastRoundWithMetadata);

          if (!matchesByRound[match.round]) {
            matchesByRound[match.round] = [];
          }
          matchesByRound[match.round].push(lastRoundWithMetadata);
        }
      }

      for (const matchGroup of Object.values(matchGroups)) {
        matchGroup.maxByRounds = Math.max(...matchGroup.matchCountByRound.filter(isPresent));
      }

      this.state.matches = nextMatches;
      this.state.matchGroups = matchGroups;
      this.state.matchesByRound = matchesByRound;
    });
  }

  get selectedCompetition(): TournamentCompetition | undefined {
    if (!this.state.selectedCompetitionId) return undefined;
    return this.state.competitions.findById(this.state.selectedCompetitionId);
  }

  async actionSelectCompetition(competitionId: string) {
    if (this.state.selectedCompetitionId !== competitionId) {
      this.state.completedRoundsExpanded = false;
    }
    this.state.selectedCompetitionId = competitionId;
    sessionStorage.setItem("selectedCompetitionId", competitionId);
  }

  get hasResolvedRounds(): boolean {
    return this.state.matchesByRound.some((roundMatches) => {
      const matches = roundMatches.flatMap((group) => group.matches || []);
      return roundMatchesResolved(matches);
    });
  }

  isRoundCollapsed(_round: number, matches: TournamentMatch[]): boolean {
    if (!roundMatchesResolved(matches)) return false;
    return !this.state.completedRoundsExpanded;
  }

  actionToggleCompletedRounds() {
    this.state.completedRoundsExpanded = !this.state.completedRoundsExpanded;
  }

  actionHighlightEntry(entryId: string) {
    this.state.highlightEntryId = entryId;
  }

  actionClearHighlight() {
    this.state.highlightEntryId = undefined;
  }

  hasOpenConflictsForSlot(slotId?: string): boolean {
    const competitionId = this.state.selectedCompetitionId;
    if (!competitionId || !slotId) return false;
    return this.tournamentController.conflictsForSlot(competitionId, slotId).length > 0;
  }

  conflictsForSlot(slotId?: string) {
    const competitionId = this.state.selectedCompetitionId;
    if (!competitionId || !slotId) return [];
    return this.tournamentController.conflictsForSlot(competitionId, slotId);
  }

  async actionSplitIntoDivisions(round: number, numDivisions: number) {
    const competition = this.selectedCompetition;
    if (!competition || !competition.id) return;

    const formatData = competition.format_data || {};
    formatData.divisionSplit = {
      round,
      numDivisions,
    };
    competition.format_data = formatData;
    await competition.save();
  }

  async actionCreateMatch(round: number, entry1Id?: string, entry2Id?: string, division?: number) {
    const competition = this.selectedCompetition;
    if (!competition || !competition.id) return;

    // Calculate match_index - find the highest match_index in this round and add 1
    const roundMatches = this.state.matches.filter((m) => m.round === round);
    const maxMatchIndex =
      roundMatches.length > 0 ? Math.max(...roundMatches.map((m) => m.match_index || 0)) : -1;

    const matchIndex = maxMatchIndex + 1;
    const slotId =
      division !== undefined ? `r${round}:m${matchIndex}:d${division}` : `r${round}:m${matchIndex}`;
    await new TournamentDataAdapter().appendEvent({
      competition,
      eventType: "match_entries_set",
      payload: {
        slot: {
          slot_id: slotId,
          round,
          match_index: matchIndex,
          division,
        },
        entries: [entry1Id, entry2Id].filter(isPresent),
        locked: true,
      },
    });
    this.tournamentController.queueCompetitionForGenerationImmediate(competition.id);
  }
}
